import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiFlag, FiCheckCircle, FiSlash, FiMessageCircle, FiAward,
  FiCalendar, FiUser, FiPrinter, FiAlertTriangle, FiArchive,
} from 'react-icons/fi';

import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  protocolosLegislativosService, TIPO_PL_LABEL, DECISAO_LABEL,
  type PLDetalhe, type TipoPL,
} from '../../services/protocoloLegislativo.service';
import { formatarDataBR, formatarDataHoraBR } from '../../lib/data';

export function ProtocoloLegislativoDetalhePublicoPage() {
  const { id } = useParams<{ id: string }>();
  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [pl, setPl] = useState<PLDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!id) return;
    setCarregando(true);
    setErro(null);
    const estabId = getEstabelecimentoAtualId();
    Promise.all([
      protocolosLegislativosService.buscar(id),
      supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', estabId).maybeSingle(),
    ])
      .then(([dados, e]) => {
        if ('erro' in dados) {
          setErro(mensagemErro(dados.erro));
        } else {
          setPl(dados);
        }
        if (e.data) setEstab(e.data as { nome: string; cor_primaria: string | null });
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [id]);

  if (carregando) {
    return (
      <PortalPage titulo="Protocolo legislativo" icone={<FiArchive size={26} />} largura="media" voltarHref="/transparencia/protocolo-legislativo" voltarLabel="Voltar à lista">
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }
  if (erro || !pl) {
    return (
      <PortalPage
        titulo="Protocolo legislativo"
        icone={<FiArchive size={26} />}
        largura="media"
        voltarHref="/transparencia/protocolo-legislativo"
        voltarLabel="Voltar à lista"
        breadcrumbs={[{ label: 'Transparência' }, { label: 'Protocolo legislativo', href: '/transparencia/protocolo-legislativo' }]}
      >
        <Alert variante="erro">{erro ?? 'Protocolo não encontrado.'}</Alert>
      </PortalPage>
    );
  }

  const tipoLabel = pl.tipo_pl ? TIPO_PL_LABEL[pl.tipo_pl as TipoPL] : 'Protocolo';
  const isVeto = pl.decisao === 'vetado';
  const isSancionado = pl.decisao === 'sancionado' || pl.decisao === 'promulgado';
  const isDevolvido = pl.decisao === 'devolvido';
  const isPendente = pl.decisao === 'pendente';
  const nomeEstab = pl.estabelecimento_nome ?? estab?.nome;
  const titulo = `${tipoLabel}${pl.numero_camara ? ` nº ${pl.numero_camara}` : ''}`;

  return (
    <PortalPage
      titulo={titulo}
      subtitulo={[nomeEstab, pl.autor_camara && `Autor: ${pl.autor_camara}`].filter(Boolean).join(' · ')}
      icone={<FiArchive size={26} />}
      largura="media"
      voltarHref="/transparencia/protocolo-legislativo"
      voltarLabel="Voltar à lista"
      breadcrumbs={[
        { label: 'Transparência' },
        { label: 'Protocolo legislativo', href: '/transparencia/protocolo-legislativo' },
        { label: titulo },
      ]}
    >
      <div className="space-y-5">
        {/* Banner da decisão */}
        {isPendente && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 print:hidden">
            <div className="flex items-center gap-2 font-semibold">
              <FiAlertTriangle size={14} /> Aguardando decisão do Executivo
            </div>
          </div>
        )}
        {isSancionado && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900 print:hidden">
            <div className="flex items-center gap-2 font-semibold mb-1">
              {pl.decisao === 'promulgado' ? <FiAward size={14} /> : <FiCheckCircle size={14} />}
              {pl.decisao === 'promulgado' ? 'Projeto promulgado' : 'Projeto sancionado'}
            </div>
            <div className="opacity-90">
              {pl.data_decisao && <>Em {formatarDataBR(pl.data_decisao)}. </>}
              {pl.ato_oficial_numero && <>Ato Oficial gerado: <strong>{pl.ato_oficial_numero}</strong>.</>}
            </div>
          </div>
        )}
        {isVeto && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-900 print:hidden">
            <div className="flex items-center gap-2 font-semibold mb-1">
              <FiSlash size={14} /> Projeto vetado{pl.veto_total === 'true' ? ' integralmente' : pl.veto_total === 'false' ? ' parcialmente' : ''}
            </div>
            <div className="opacity-90">
              {pl.data_decisao && <span className="block">Em {formatarDataBR(pl.data_decisao)}.</span>}
              {pl.veto_motivo && <span className="block mt-1"><strong>Motivo:</strong> {pl.veto_motivo}</span>}
            </div>
          </div>
        )}
        {isDevolvido && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900 print:hidden">
            <div className="flex items-center gap-2 font-semibold mb-1">
              <FiMessageCircle size={14} /> Resposta devolvida à Câmara
            </div>
            <div className="opacity-90">
              {pl.data_decisao && <span className="block">Em {formatarDataBR(pl.data_decisao)}.</span>}
              {pl.resposta_executivo && <span className="block mt-1">{pl.resposta_executivo}</span>}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" onClick={() => window.print()}>
            <FiPrinter size={14} /> Imprimir
          </Button>
        </div>

        <SectionCard>
          <div className="text-center mb-6 pb-4 border-b border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
              {tipoLabel}{pl.numero_camara && <> nº {pl.numero_camara}</>}
            </div>
            <h2 className="text-base font-semibold text-slate-900">{pl.titulo}</h2>
            {pl.autor_camara && (
              <p className="text-xs text-slate-600 mt-1">Autoria: {pl.autor_camara}</p>
            )}
          </div>

          {pl.ementa && (
            <div className="mb-6 pl-6 border-l-2 border-slate-300">
              <p className="text-sm text-slate-700 italic leading-relaxed">{pl.ementa}</p>
            </div>
          )}

          {pl.corpo && (
            <div
              className="prose prose-slate prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: pl.corpo }}
            />
          )}

          <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 print:hidden">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {pl.data_protocolo_camara && (
                <span className="inline-flex items-center gap-1">
                  <FiCalendar size={11} /> Protocolo Câmara: {formatarDataBR(pl.data_protocolo_camara)}
                </span>
              )}
              {pl.data_recebimento_executivo && (
                <span className="inline-flex items-center gap-1">
                  <FiCalendar size={11} /> Recebido pelo Executivo: {formatarDataBR(pl.data_recebimento_executivo)}
                </span>
              )}
              {pl.autor_camara && (
                <span className="inline-flex items-center gap-1">
                  <FiUser size={11} /> {pl.autor_camara}
                </span>
              )}
            </div>
          </div>
        </SectionCard>

        {/* Histórico público */}
        {pl.historico && pl.historico.length > 0 && (
          <SectionCard titulo={`Tramitação (${pl.historico.length})`} icone={<FiFlag size={18} />} className="print:hidden">
            <ol className="space-y-3">
              {pl.historico.map((h, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-slate-300 mt-2"></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900">{h.titulo ?? h.tipo}</div>
                    {h.mensagem && <div className="text-slate-600 text-xs mt-0.5">{h.mensagem}</div>}
                    <div className="text-slate-400 text-xs mt-1">
                      {formatarDataHoraBR(h.criado_em)}{h.ator_nome && ` · ${h.ator_nome}`}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </SectionCard>
        )}

        <div className="text-xs text-slate-500 text-center print:hidden">
          Decisão atual: <strong>{DECISAO_LABEL[pl.decisao]}</strong>. Endereço permanente: <span className="font-mono">{window.location.href}</span>
        </div>
      </div>
    </PortalPage>
  );
}

const ERROS: Record<string, string> = {
  nao_encontrado: 'Protocolo não encontrado ou removido.',
  modulo_invalido: 'Documento inválido.',
};

function mensagemErro(c: string): string {
  return ERROS[c] ?? 'Não foi possível carregar o protocolo.';
}
